// ─── MONEY ───────────────────────────────────────────────
export const money = (n) => Math.round((Number(n) || 0) * 100) / 100;

// ─── THEME ───────────────────────────────────────────────
const hexToRgb = (h) => {
  const m = /^#?([0-9a-f]{6})$/i.exec(String(h || '').trim());
  if (!m) return null;
  const n = parseInt(m[1], 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
};
const rgbLum = ([r, g, b]) => (0.2126 * r + 0.7152 * g + 0.0722 * b) / 255;
const mixRgb = (a, b, t) => a.map((v, i) => Math.round(v * t + b[i] * (1 - t)));
const rgbHex = (rgb) => '#' + rgb.map(v => v.toString(16).padStart(2, '0')).join('');

// The single place the theme becomes CSS variables. A palette only names --primary and the
// sidebar colours, so the tints the UI uses (--primary-50/100/200) and the sidebar title
// colours are derived here; nothing else should hardcode a theme colour.
export function applyTheme(vars) {
  if (!vars) return;
  const out = { ...vars };
  const primary = hexToRgb(vars['--primary']);
  if (primary) {
    const bg = hexToRgb(vars['--bg']);
    const dark = bg ? rgbLum(bg) < 0.35 : false;
    const base = dark ? (hexToRgb(vars['--card-bg']) || [30, 41, 59]) : [255, 255, 255];
    const strength = dark ? [0.18, 0.30, 0.45] : [0.12, 0.28, 0.46];
    ['--primary-50', '--primary-100', '--primary-200'].forEach((k, i) => {
      if (!(k in vars)) out[k] = rgbHex(mixRgb(primary, base, strength[i]));
    });
  }
  const sidebar = hexToRgb(vars['--sidebar-bg']);
  if (sidebar) {
    const darkSidebar = rgbLum(sidebar) < 0.5;
    if (!('--sidebar-title' in vars)) out['--sidebar-title'] = darkSidebar ? '#FFFFFF' : 'var(--text-primary)';
    if (!('--sidebar-tag' in vars)) out['--sidebar-tag'] = darkSidebar ? 'var(--sidebar-text)' : 'var(--primary)';
  }
  Object.entries(out).forEach(([k, v]) => document.documentElement.style.setProperty(k, v));
}

// ─── ID GENERATORS ───────────────────────────────────────
export function genRollNo(settings, cls, count) {
  const prefix = cls ? cls.replace('Grade ','G').replace('Nursery','NUR').replace('Kindergarten','KG') : 'STU';
  return (settings.rollFormat || 'CLASS-###').replace('CLASS', prefix).replace(/#+/g, m => String(count).padStart(m.length, '0'));
}

export function genFamilyId(settings, count) {
  return (settings.familyIdFormat || 'FAM-####').replace(/#+/g, m => String(count).padStart(m.length, '0'));
}

export function genAdmissionNo(settings, count) {
  return (settings.admissionNoFormat || 'ADM-####').replace(/#+/g, m => String(count).padStart(m.length, '0'));
}

export function normalizeDate(v) {
  if (v == null) return '';
  const s = String(v).trim();
  if (s === '') return '';
  let y, m, d, x;
  if (/^\d{5}$/.test(s)) {
    return new Date(Date.UTC(1899, 11, 30) + Number(s) * 86400000).toISOString().slice(0, 10);
  }
  if ((x = s.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/))) { y = +x[1]; m = +x[2]; d = +x[3]; }
  else if ((x = s.match(/^(\d{1,2})[\/.-](\d{1,2})[\/.-](\d{4})$/))) { d = +x[1]; m = +x[2]; y = +x[3]; }
  else return null;
  const dt = new Date(Date.UTC(y, m - 1, d));
  if (dt.getUTCFullYear() !== y || dt.getUTCMonth() !== m - 1 || dt.getUTCDate() !== d) return null;
  return dt.toISOString().slice(0, 10);
}

// ─── FEE CALCULATIONS ────────────────────────────────────
export function computeNetFee(grossAmount, discount) {
  if (!discount || !discount.type) return grossAmount;
  const off = discount.type === 'percent'
    ? grossAmount * (discount.value / 100)
    : discount.value;
  return Math.max(0, grossAmount - off);
}

export function calcStudentMonthlyFee(student, settings) {
  if (!settings?.feeHeads) return 0;
  return settings.feeHeads
    .filter(h => h.type === 'monthly')
    .reduce((a, h) => a + computeNetFee(Number(student.fees?.[h.id] || 0), student.feeDiscounts?.[h.id]), 0);
}

// ─── GROUPING ────────────────────────────────────────────
export function groupByFamily(students) {
  const f = {};
  students.forEach(s => {
    const k = s.familyId || String(s.id);
    if (!f[k]) f[k] = [];
    f[k].push(s);
  });
  return f;
}

// ─── NUMBER TO WORDS ─────────────────────────────────────
export function numToWords(n) {
  const a = ['','One','Two','Three','Four','Five','Six','Seven','Eight','Nine','Ten','Eleven','Twelve','Thirteen','Fourteen','Fifteen','Sixteen','Seventeen','Eighteen','Nineteen'];
  const b = ['','','Twenty','Thirty','Forty','Fifty','Sixty','Seventy','Eighty','Ninety'];
  if (n === 0) return 'Zero';
  if (n < 20) return a[n];
  if (n < 100) return b[Math.floor(n/10)] + (n%10 ? ' ' + a[n%10] : '');
  if (n < 1000) return a[Math.floor(n/100)] + ' Hundred' + (n%100 ? ' ' + numToWords(n%100) : '');
  if (n < 100000) return numToWords(Math.floor(n/1000)) + ' Thousand' + (n%1000 ? ' ' + numToWords(n%1000) : '');
  return numToWords(Math.floor(n/100000)) + ' Lakh' + (n%100000 ? ' ' + numToWords(n%100000) : '');
}

// ─── DATE HELPERS ────────────────────────────────────────
export function monthKey(m, y) { return `${y}-${String(m+1).padStart(2,'0')}`; }
export function monthLabel(m, y) {
  const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];
  return `${MONTHS[m]} ${y}`;
}
export function parseMonthKey(k) {
  const [y, m] = k.split('-');
  return { year: Number(y), month: Number(m)-1 };
}
export function getNextMonthKey(mk) {
  const { year, month } = parseMonthKey(mk);
  if (month === 11) return monthKey(0, year+1);
  return monthKey(month+1, year);
}
export function getPrevMonthKey(mk) {
  const { year, month } = parseMonthKey(mk);
  if (month === 0) return monthKey(11, year-1);
  return monthKey(month-1, year);
}

// ─── STUDENT STATUS MAPPER ───────────────────────────────
export function mapStudentStatus(dbStatus) {
  const map = {
    active:'Active', inactive:'Inactive', left:'Left',
    suspended:'Suspended', transferred:'Transferred', expelled:'Expelled',
    on_leave:'On Leave', passed_out:'Passed Out'
  };
  return map[dbStatus] || 'Active';
}

export const STATUS_TO_DB = {
  'Active':'active', 'Inactive':'inactive', 'Left':'left',
  'Suspended':'suspended', 'Passed Out':'passed_out',
  'Transferred':'transferred', 'Expelled':'expelled', 'On Leave':'on_leave',
};

// ─── MAP DB STUDENT TO LOCAL ──────────────────────────────
export function mapDbStudent(s) {
  return {
    id: s.id,
    admissionNo: s.admission_no,
    roll: s.roll_no,
    familyId: s.families?.family_id_display || '',
    familyUuid: s.families?.id || s.family_id || null,
    name: s.name,
    class: s.classes?.name || '',
    classId: s.grade_id || null,
    section: s.sections?.name || '',
    sectionId: s.section_id || null,
    status: mapStudentStatus(s.status),
    dob: s.dob || '',
    gender: s.gender ? s.gender.charAt(0).toUpperCase() + s.gender.slice(1) : 'Male',
    religion: s.religion || 'Islam',
    nationality: s.nationality || 'Pakistani',
    cast: s.caste || '',
    bform: s.bform_no || '',
    fatherName: s.families?.father_name || '',
    fatherCnic: s.families?.father_cnic || '',
    fatherOccupation: s.families?.father_occupation || '',
    fatherEducation: s.families?.father_education || '',
    fatherIncome: s.families?.father_income || '',
    motherName: s.families?.mother_name || '',
    motherCnic: s.families?.mother_cnic || '',
    motherOccupation: s.families?.mother_occupation || '',
    guardianName: s.families?.guardian_name || '',
    guardianRelation: s.families?.guardian_relation || '',
    guardianCnic: s.families?.guardian_cnic || '',
    phone: s.families?.phone || '',
    whatsapp: s.families?.whatsapp || '',
    motherPhone: s.families?.mother_phone || '',
    motherPhone2: s.families?.mother_phone2 || '',
    emergencyContact: s.families?.emergency_contact || '',
    address: s.families?.address || '',
    area: s.families?.area || '',
    previousSchool: s.previous_school || '',
    lastClassPassed: s.last_class_passed || '',
    tcNo: s.tc_no || '',
    reasonLeaving: s.reason_leaving_prev || '',
    admissionDate: s.admission_date || '',
    bloodGroup: s.blood_group || 'Unknown',
    allergies: s.allergies || '',
    conditions: s.conditions || '',
    disability: s.disability || 'None',
    doctorName: s.doctor_name || '',
    doctorContact: s.doctor_contact || '',
    vaccination: s.vaccination_status || '',
    discountType: s.discount_type || '',
    discountAmount: s.discount_amount || 0,
    notes: s.notes || '',
    counsellingNotes: s.counselling_notes || '',
    photoUrl: s.photo_url || null,
    leavingDate: s.leaving_date || '',
    leavingReason: s.leaving_reason || '',
    fees: (() => {
      const f = {};
      (s.student_fee_assignments || [])
        .filter(a => a.is_active !== false)
        .forEach(a => { f[a.fee_head_id] = Number(a.amount) || 0; });
      return f;
    })(),
    feeDiscounts: (() => {
      const d = {};
      (s.student_fee_assignments || [])
        .filter(a => a.is_active !== false && a.discount_type)
        .forEach(a => { d[a.fee_head_id] = { type: a.discount_type, value: Number(a.discount_value) || 0 }; });
      return d;
    })(),
  };
}
